/**
 * Travel Impact Calculator, daily-quota gate banner.
 *
 * Renders when the public batch endpoint returns 429 with code
 * `quota_exceeded` (the visitor has used their 20 calculations for
 * the day). Replaces the page-level red error banner: a user who has
 * run twenty calculations is the calculator's highest-intent
 * visitor, not someone who broke the page, so this banner is a
 * constructive reframe rather than an error.
 *
 * Design notes:
 *  - Informational only. The actual signup CTA lives in
 *    `MeetJodyCTA` directly below this banner on the page (mounted
 *    by `TripCalculatorPage` whenever the gate is up). Keeping the
 *    conversion affordance in one place avoids duplicating it across
 *    two surfaces, and makes the page read as "you hit a limit, but
 *    here is what comes next."
 *  - Mint/emerald palette, no red, no alert iconography. Sits in the
 *    same visual family as `MeetJodyCTA` so the two surfaces feel
 *    like one continuous answer.
 *  - Shows the exact reset time computed from the backend's
 *    `retryAfterSeconds`, rendered in the visitor's local timezone.
 */

import React from 'react';

export interface QuotaExceededCardProps {
  /**
   * Absolute timestamp (ms since epoch) at which the per-IP daily
   * quota resets. Derived by the page from the backend's
   * `retryAfterSeconds` at the moment of the 429.
   */
  resetAt: number;
}

const QuotaExceededCard: React.FC<QuotaExceededCardProps> = ({ resetAt }) => {
  const resetTimeLabel = formatResetTime(resetAt);

  return (
    <section
      aria-label="Daily limit reached"
      style={{
        marginTop: '1rem',
        padding: '1.1rem 1.25rem',
        background: 'linear-gradient(160deg, #ecfdf5 0%, #d1fae5 100%)',
        border: '1px solid #a7f3d0',
        borderRadius: 12,
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 12px',
          background: 'rgba(4, 120, 87, 0.12)',
          border: '1px solid rgba(4, 120, 87, 0.22)',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          color: '#047857',
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}
      >
        <span aria-hidden>🌱</span>
        <span>Daily limit reached</span>
      </div>

      <h2
        style={{
          fontSize: '1.15rem',
          fontWeight: 700,
          color: '#064e3b',
          margin: '0.75rem 0 0.5rem',
          lineHeight: 1.3,
        }}
      >
        You've used your 20 calculations for today.
      </h2>

      <p
        style={{
          margin: 0,
          fontSize: 14.5,
          color: '#0f172a',
          lineHeight: 1.55,
        }}
      >
        This is our daily limit per visitor, so the Travel Impact Calculator stays free
        and fast for everyone. Your counter resets at <b>{resetTimeLabel}</b>.
      </p>

      <p
        style={{
          margin: '0.9rem 0 0',
          fontSize: 14.5,
          color: '#0f172a',
          lineHeight: 1.55,
        }}
      >
        In a hurry? <b>Jody</b>, our intelligent travel planner for more sustainable
        travels, has no daily limit and does much more for the way you travel. See what
        Jody can do for you just below, and create your free account to start planning.
      </p>
    </section>
  );
};

export default QuotaExceededCard;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders the reset moment as a short, local-time string. Two shapes
 * depending on whether the reset falls on the same calendar day:
 *  - same day:  "3:42 PM"
 *  - next day:  "tomorrow at 8:15 AM"
 *
 * We deliberately avoid UTC framing in the visible string. Visitors
 * shouldn't have to do timezone math to know when they can try again,
 * and "midnight UTC" is, in any case, wrong (the express-rate-limit
 * window is anchored to the user's first request of the day, not the
 * UTC calendar boundary).
 */
function formatResetTime(resetAt: number): string {
  const now = new Date();
  const reset = new Date(resetAt);
  const time = reset.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const sameCalendarDay =
    now.getFullYear() === reset.getFullYear() &&
    now.getMonth() === reset.getMonth() &&
    now.getDate() === reset.getDate();
  return sameCalendarDay ? time : `tomorrow at ${time}`;
}
